import { PERMISSION } from '../constants';
import { User, UserLocationOption } from '../services/types';
import { buildRedirectUrl } from './location';

const REFRESH_TOKEN_STORAGE_KEY = "refreshToken";
const ACCESS_TOKEN_STORAGE_KEY = "accessToken";
const USER_STORAGE_KEY = "user";
const SESSION_EXPIRES_AT_MS_KEY = "sessionExpiresAtMs";
export const USER_LOCATION_OPTIONS_STORAGE_KEY = 'userLocationOptions';

export const logout = () => {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem(SESSION_EXPIRES_AT_MS_KEY);
  localStorage.removeItem(USER_LOCATION_OPTIONS_STORAGE_KEY);
  window.location.href = buildRedirectUrl("/login");
}

export function getCachedUserLocations(): UserLocationOption[] | null {
  try {
    const raw = localStorage.getItem(USER_LOCATION_OPTIONS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as UserLocationOption[]) : null;
  } catch {
    return null;
  }
}

export function setCachedUserLocations(options: UserLocationOption[]) {
  localStorage.setItem(USER_LOCATION_OPTIONS_STORAGE_KEY, JSON.stringify(options));
}

export function clearCachedUserLocations() {
  localStorage.removeItem(USER_LOCATION_OPTIONS_STORAGE_KEY);
}

export const getUser = (): User | null => {
  const user = localStorage.getItem(USER_STORAGE_KEY);
  return user ? JSON.parse(user) : null;
}

export const storeUser = (user: User) => {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

export const isAdmin = (): boolean => {
  const user = getUser();
  return user?.permissions.includes(PERMISSION.ADMIN) || false;
}

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
}

const setAccessToken = (token: string) => {
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
}

const setRefreshToken = (token: string) => {
  if (!token) return;

  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
}

function applySessionExpiryFromSeconds(expiryInSecond?: number) {
  if (!expiryInSecond || typeof expiryInSecond !== "number" || !Number.isFinite(expiryInSecond) || expiryInSecond <= 0) {
    return;
  }

  localStorage.setItem(SESSION_EXPIRES_AT_MS_KEY, String(Date.now() + Math.floor(expiryInSecond * 1000)));
}

/** True when a stored access-session deadline exists and is in the past. Missing storage is treated as not expired (older sessions before this field existed). */
export function isAccessSessionExpired(): boolean {
  const raw = localStorage.getItem(SESSION_EXPIRES_AT_MS_KEY);
  if (raw == null) return false;

  const ms = Number(raw);
  if (!Number.isFinite(ms)) return false;
  
  return Date.now() >= ms;
}

export const storeToken = (accessToken: string, refreshToken: string, expiryInSecond?: number) => {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
  applySessionExpiryFromSeconds(expiryInSecond);
}

export const isAuthenticated = () => {
  return !!getUser() && !!getRefreshToken() && !isAccessSessionExpired();
}